"use client";

import { useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";

import { useLang } from "@/hooks/use-lang";

import CustomDatafeed from "./CustomDatafeed";
import { LocalStorageSaveLoadAdapter } from "./LocalStorageSaveLoadAdapter";
import style from "./style.module.scss";

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    TradingView: any;
    Datafeeds: any;
  }
}

interface TradingViewAdvancedChartProps {
  symbol: string;
  interval?: string;
  theme?: "light" | "dark";
  debug?: boolean;
}

const localeMap = {
  en: "en",
  zh: "zh_TW",
};

export function TradingViewAdvancedChart({
  symbol,
  interval = "1D",
  theme = "dark",
  debug = true,
}: TradingViewAdvancedChartProps) {
  const { lang, t } = useLang();
  const widgetRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Separate the script loading logic
  const loadTradingViewScripts = async () => {
    // Check if scripts are already loaded
    if (window.TradingView && window.Datafeeds) {
      return true;
    }

    try {
      // Load scripts dynamically if needed
      await Promise.all([
        new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "/tradingview/charting_library/charting_library.js";
          script.async = true;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        }),
        new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "/tradingview/datafeeds/udf/dist/bundle.js";
          script.async = true;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        }),
      ]);
      return true;
    } catch (error) {
      console.error("Failed to load TradingView scripts:", error);
      return false;
    }
  };

  const initWidget = async () => {
    if (!containerRef.current) return;

    // Ensure scripts are loaded before initializing
    const scriptsLoaded = await loadTradingViewScripts();
    if (!scriptsLoaded) return;

    // Clear container before initializing
    containerRef.current.innerHTML = "";

    // get datafeed by UDFCompatibleDatafeed
    // if (!window.Datafeeds) return;
    // const datafeed = new window.Datafeeds.UDFCompatibleDatafeed(
    //   `${NIKE_URL}/tradingview-chart`
    // );

    const containerId = `tradingview-widget-container-${symbol}-${Date.now()}`;
    containerRef.current.id = containerId;

    const widgetOptions = {
      container: containerId,
      locale: localeMap[lang],
      library_path: "/tradingview/charting_library/",
      theme,
      datafeed: CustomDatafeed,
      symbol,
      interval,
      fullscreen: true,
      debug,
      autosize: true,
      enabled_features: ["study_templates", "drawing_templates"],
      save_load_adapter: new LocalStorageSaveLoadAdapter(),
    };

    // Cleanup existing widget if it exists
    if (widgetRef.current) {
      widgetRef.current.remove();
      widgetRef.current = null;
    }
    widgetRef.current = new window.TradingView.widget(widgetOptions);
  };

  useEffect(() => {
    initWidget();
    return () => {
      if (widgetRef.current) {
        widgetRef.current.remove();
      }
      if (window.TradingView) {
        delete window.TradingView;
      }
      if (window.Datafeeds) {
        delete window.Datafeeds;
      }
    };
  }, []);

  useEffect(() => {
    // get the height of navbar and event banner
    const navbarHeight = document.querySelector("header")?.clientHeight;
    const eventBannerHeight =
      document.querySelector("#event-banner")?.clientHeight || 0;
    const totalMarginTop = `calc(${navbarHeight}px + ${eventBannerHeight}px)`;
    // set the total height for css variable
    document.documentElement.style.setProperty(
      "--total-margin-top",
      totalMarginTop
    );
  }, []);

  return (
    <div className={twMerge("bg-zinc-900", style.widgetContainer)}>
      <div ref={containerRef} />
      <div data-testid="tradingview-disclaimer" className={style.disclaimer}>
        {t.tradingview.disclaimer}
      </div>
    </div>
  );
}
